import { Box, CircularProgress, CssBaseline } from "@mui/material";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { init, tanstackRouterBrowserTracingIntegration } from "@sentry/react";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { formDevtoolsPlugin } from "@tanstack/react-form-devtools";
import { hotkeysDevtoolsPlugin } from "@tanstack/react-hotkeys-devtools";
import { PacerDevtoolsPanel } from "@tanstack/react-pacer-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { createRouter, Navigate, RouterProvider } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { Settings } from "luxon";
import * as React from "react";
import { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import * as z from "zod/v4";
import { ApplicationProvider } from "./app/context/applicationContext";
import { CableProvider } from "./app/context/cableContext";
import { UserProvider } from "./app/context/userContext";
import store from "./app/reducers/singletonStore";
import { useTheme } from "./app/views/theme";
import { routeTree } from "./routeTree.gen";

Settings.defaultLocale = "sv";
z.config(z.locales.sv());

const queryClient = new QueryClient({});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: { queryClient },
  defaultNotFoundComponent: () => {
    return Navigate({
      to: "/dashboard",
    });
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const reloadOnError = (event: Event & { payload?: { message: string } }) => {
  if (!event.payload) {
    return;
  }
  console.log(event);
  const retried = sessionStorage.getItem(`errRetries${event.payload.message}`);
  if (retried) {
    return;
  }
  sessionStorage.setItem(`errRetries${event.payload.message}`, "1");
  window.location.reload();
};

window.addEventListener("vite:preloadError", (event) => {
  reloadOnError(event);
});

const Main: React.FC = () => {
  const theme = useTheme();
  return (
    <React.StrictMode>
      <Provider store={store}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="sv">
              <QueryClientProvider client={queryClient}>
                <UserProvider>
                  <CableProvider>
                    <ApplicationProvider>
                      <Box
                        sx={(theme) => ({
                          display: "flex",
                          flexGrow: 1,
                          backgroundColor: theme.palette.grey[900],
                          height: "100vh",
                          overflow: "auto",
                          ...theme.applyStyles("light", {
                            backgroundColor: theme.palette.grey[100],
                          }),
                        })}
                      >
                        <Suspense fallback={<CircularProgress />}>
                          <CssBaseline enableColorScheme />
                          <RouterProvider router={router} />
                        </Suspense>
                      </Box>
                    </ApplicationProvider>
                  </CableProvider>
                </UserProvider>
                <TanStackDevtools
                  plugins={[
                    {
                      name: "React Query",
                      render: <ReactQueryDevtoolsPanel />,
                    },
                    {
                      name: "Router",
                      render: <TanStackRouterDevtoolsPanel router={router} />,
                    },
                    formDevtoolsPlugin(),
                    {
                      name: "Pacer",
                      render: <PacerDevtoolsPanel />,
                    },
                    hotkeysDevtoolsPlugin(),
                  ]}
                />
              </QueryClientProvider>
            </LocalizationProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </Provider>
    </React.StrictMode>
  );
};

const apiUrl = import.meta.env.VITE_API_URL;

init({
  dsn:
    import.meta.env.VITE_SENTRY_DSN ||
    "https://435e919d954c0f4484ecdfc42467b478@o4509249201700864.ingest.de.sentry.io/4509249216118864",
  environment: import.meta.env.DEV ? "development" : "production",
  integrations: [
    tanstackRouterBrowserTracingIntegration(router),
    // feedbackIntegration({
    //   colorScheme: "system",
    // }),
  ],
  tracesSampleRate: import.meta.env.DEV ? 0 : 0.2,
  // Send trace headers to the Rails API (a separate origin outside local dev) so backend spans join the frontend trace
  tracePropagationTargets: [
    "localhost",
    /^\/(?!\/)/,
    ...(apiUrl?.startsWith("http") ? [apiUrl] : []),
  ],
});

const root = ReactDOM.createRoot(document.getElementById("root")!);
root.render(<Main />);
